import { inject, Injectable } from "@angular/core";
import {
    collection,
    collectionData,
    doc,
    DocumentReference,
    DocumentSnapshot,
    Firestore,
    onSnapshot
} from "@angular/fire/firestore";
import { Data } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { catchError, combineLatest, forkJoin, map, Observable, of, shareReplay, take } from "rxjs";

@Injectable({
    providedIn: "root",
})
export class ProductService {
    private firestore = inject(Firestore);
    private authService = inject(AuthService);

    public fetchProductData(productName: string): Observable<Data> {
        const userId = this.authService.userIdSignal();

        if (!userId) {
            return of(null);
        }

        return this.fetchStoreDocument(userId, productName);
    }

    private fetchStoreDocument(userId: string, productName: string): Observable<Data> {
        const docRef = doc(this.firestore, `/users/${userId}/product_page_onload/${productName}`);

        const product$ = this.createDocumentObservable(docRef).pipe(map((snapshot) => this.extractDocumentData(snapshot)), take(1), shareReplay(1), catchError(() => of(null)));
        const offers$ = this.fetchStoreOffers(productName).pipe(take(1), shareReplay(1), catchError(() => of(null)));

        return forkJoin({ product: product$, offers: offers$ }).pipe(
            map(({ product: productData, offers: offersData }) => {
                if (!productData) {
                    return null;
                }

                return {
                    ...productData,
                    ...offersData,
                };
            }),
            shareReplay(1)
        );
    }

    fetchStoreOffers(productName: string): Observable<Data> {
        const paths = [
            `general_modules/store_offers_v1/${productName}/2_weeks/offers`,
            `general_modules/store_offers_v1/${productName}/4_weeks/offers`,
            `general_modules/store_offers_v1/${productName}/8_weeks/offers`,
            `general_modules/store_offers_v1/${productName}/credit/offers`,
            `general_modules/store_offers_v1/${productName}/oneoff/offers`,
            `general_modules/store_offers_v1/${productName}/subscription/offers`,
        ];

        const observables = paths.map((path) => {
            const colRef = collection(this.firestore, path);
            return collectionData(colRef, { idField: "id" });
        });

        return combineLatest(observables).pipe(
            map((res: Data) => {
                return {
                    "2_weeks": res[0],
                    "4_weeks": res[1],
                    "8_weeks": res[2],
                    credit: res[3],
                    oneoff: res[4],
                    subscription: res[5],
                };
            }),
            take(1),
            shareReplay(1)
        );
    }

    private createDocumentObservable(docRef: DocumentReference): Observable<DocumentSnapshot> {
        return new Observable((observer) => {
            const unsubscribe = onSnapshot(
                docRef,
                (snapshot) => observer.next(snapshot),
                (error) => observer.error(error)
            );
            return () => unsubscribe();
        });
    }

    private extractDocumentData(snapshot: Data) {
        if (!snapshot || snapshot?.isOfflinePlaceholder) {
            return null;
        }

        if (typeof snapshot?.data === "function") {
            return snapshot.data();
        }

        return snapshot;
    }
}